import { Link } from "react-router-dom";
import { Badge } from "~/components/ui/badge";
import useNextGenHeader from "~/contexts/nextgen-header";
import { usePageContext } from "~/contexts/page-context";
import { useDocumentTitle } from "~/hooks/useDocumentTitle";
import { useFetcher } from "~/hooks/useFetcher";
import { NEXTGEN_MAX_CONTENT_WIDTH } from "~/lib/layout";
import { ListLoadError } from "~/pages/manifold-nextgen/components/ListLoadError";
import { ListLoadingSkeleton } from "~/pages/manifold-nextgen/components/ListLoadingSkeleton";
import { StatusMessage } from "~/pages/manifold-nextgen/components/StatusMessage";
import { KnowledgeArticleSummary, knowledgeBasePath } from "~/services/knowledgeBase";

/**
 * The knowledge base index — every article the platform agent answers from,
 * listed for a person to read directly.
 *
 * Unlinked from the nav on purpose: the corpus is written for the agent first,
 * and this page exists so we can read and review what it is being told.
 */
export const KnowledgeBase = () => {
  useDocumentTitle("Knowledge base");
  useNextGenHeader({ title: "Knowledge base" });
  usePageContext({ page: "kb" });

  const {
    data: articles,
    isLoading,
    error,
  } = useFetcher<KnowledgeArticleSummary[]>(knowledgeBasePath());

  return (
    <div className="mx-auto w-full px-6 py-8" style={{ maxWidth: NEXTGEN_MAX_CONTENT_WIDTH }}>
      <p className="mb-6 text-sm text-muted-foreground">
        How Manifold works, in short articles. These are the same articles the help agent reads when
        it answers a question.
      </p>

      {isLoading && <ListLoadingSkeleton columns={[{ width: "w-16", rounded: true }]} />}

      {!isLoading && error && <ListLoadError error={error} resourceLabel="articles" />}

      {!isLoading && !error && articles?.length === 0 && (
        <StatusMessage
          variant="empty"
          title="No articles yet"
          body="The knowledge base is empty."
        />
      )}

      {!isLoading && !error && articles && articles.length > 0 && (
        <ul className="flex flex-col gap-2">
          {articles.map((article) => (
            <li key={article.slug}>
              <Link
                to={article.slug}
                className="flex items-start gap-4 rounded-lg border border-border bg-card px-4 py-3 transition-colors hover:bg-accent"
              >
                <div className="flex flex-1 flex-col gap-1">
                  <span className="text-sm font-medium text-foreground">{article.title}</span>
                  <span className="text-sm text-muted-foreground">{article.summary}</span>
                </div>
                {article.status === "draft" && (
                  <Badge variant="warning" className="mt-0.5">
                    Draft
                  </Badge>
                )}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
